import { useGetInfluencerStore } from "@entities/discovery/lib/store/useGetInfluencerStore";
import CustomMultiSelect from "@entities/discovery/ui/CustomMultiSelect";
import CustomStar from "@entities/discovery/ui/CustomStart";
import RangeSlider from "@entities/discovery/ui/RangeSlider";
import { insightsiqPlatformWorkIdMap } from "@entities/global-discovery/constants";
import { CheckboxFilter } from "@entities/global-discovery/ui/Filters/CheckboxFilter";
import { InputNumberFilter } from "@entities/global-discovery/ui/Filters/InputNumberFilter";
import { ListFilter } from "@entities/global-discovery/ui/Filters/ListFilter";
import { MultiListFilter } from "@entities/global-discovery/ui/Filters/MultiListFilter";
import { RangeFilter } from "@entities/global-discovery/ui/Filters/RangeFilter";
import { SelectRangeFilter } from "@entities/global-discovery/ui/Filters/SelectRangeFilter";
import { TagsFilter } from "@entities/global-discovery/ui/Filters/TagsFilter";
import { ConfigGlobalTypeSingle, ConfigTypeSingle, IGFilters, OtherFilters, YtFilters } from "@shared/lib/types";
import { SocialMediaPlatform } from "@shared/lib/types/influencer";
import HelpPopOver from "@shared/ui/HelpPopover";
import { useMemo } from "react";
import styles from "./InfluencerFilters.module.scss";

interface InfluencerFiltersProps {
  isSidebar?: boolean;
}

const FILTER_TYPE_LABELS: Record<string, string> = {
  creator: "Creator",
  content: "Content",
  performance: "Performance",
  audience: "Audience",
};

export function InfluencerFilters({ isSidebar = false }: InfluencerFiltersProps) {
  const { platform, facets, selectedFilters, updateFilters } = useGetInfluencerStore();

  const filtersToSelect = useMemo(() => {
    return platform === SocialMediaPlatform.YouTube
      ? YtFilters.filter((val) => !val.hideFilter)
      : platform === SocialMediaPlatform.Instagram
      ? IGFilters.filter((val) => !val.hideFilter)
      : OtherFilters.filter((val) => !val.hideFilter);
  }, [platform]);

  // Group filters by fType, filtering out the hidden ones that are already in the table search header
  const groupedFilters = useMemo(() => {
    const groups: Record<string, Array<ConfigGlobalTypeSingle | ConfigTypeSingle>> = {};
    filtersToSelect.forEach((filter) => {
      const fType = filter.fType || "other";
      if (!groups[fType]) {
        groups[fType] = [];
      }
      groups[fType].push(filter);
    });
    return groups;
  }, [filtersToSelect]);

  // Bridge custom setSelectedFilter calls to synchronize updates to useGetInfluencerStore
  const handleSetSelectedFilter = (updateFn: (prev: any) => any) => {
    const nextFilters = updateFn(selectedFilters);
    
    // Update modified or new keys
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (key === "filterTags") return;
      if (JSON.stringify(selectedFilters[key]) !== JSON.stringify(value)) {
        updateFilters(key, value as any);
      }
    });

    // Remove deleted keys
    Object.keys(selectedFilters).forEach((key) => {
      if (nextFilters[key] === undefined) {
        updateFilters(key, undefined);
      }
    });
  };

  const handleClearAll = () => {
    Object.keys(selectedFilters).forEach((key) => {
      updateFilters(key, undefined);
    });
  };

  const getFilter = (filter: any, key: number) => {
    const platformId =
      platform === SocialMediaPlatform.YouTube
        ? insightsiqPlatformWorkIdMap.youtube
        : platform === SocialMediaPlatform.Instagram
        ? insightsiqPlatformWorkIdMap.instagram
        : undefined;

    // Helper to resolve options for list filters from facets
    const getOptions = (facetName: string) => {
      if (facets[facetName] === undefined) return undefined;
      return facets[facetName].map((d: any) => ({
        label: d._id,
        value: d._id,
      }));
    };

    switch (filter.compType) {
      case "checkbox":
        return (
          <CheckboxFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
          />
        );
      case "range":
      case "inputRange":
        return (
          <RangeFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
          />
        );
      case "inputNumber":
        return (
          <InputNumberFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
          />
        );
      case "list":
        if (filter.type === "tags") {
          return (
            <TagsFilter
              key={key}
              filter={filter}
              selectedFilters={selectedFilters}
              setSelectedFilter={handleSetSelectedFilter}
              options={getOptions(filter.facetName)}
            />
          );
        } else if (filter.type === "multiple") {
          return (
            <MultiListFilter
              key={key}
              filter={filter}
              selectedFilters={selectedFilters}
              setSelectedFilter={handleSetSelectedFilter}
              platformId={platformId}
              options={getOptions(filter.facetName)}
            />
          );
        } else {
          return (
            <ListFilter
              key={key}
              filter={filter}
              selectedFilters={selectedFilters}
              setSelectedFilter={handleSetSelectedFilter}
              options={getOptions(filter.facetName)}
            />
          );
        }
      case "multiList":
        return (
          <MultiListFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
            platformId={platformId}
            options={getOptions(filter.facetName)}
          />
        );
      case "tags":
        return (
          <TagsFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
            options={getOptions(filter.facetName)}
          />
        );
      case "selectRange":
        return (
          <SelectRangeFilter
            key={key}
            filter={filter}
            selectedFilters={selectedFilters}
            setSelectedFilter={handleSetSelectedFilter}
            platformId={platformId}
          />
        );
      case "custom-multi-select":
        return (
          <div className={styles.filterWrapper} key={key}>
            <label className={styles.label}>
              {filter.title}
              {filter.desc && <HelpPopOver customText={filter.desc} color="black" />}
            </label>
            <CustomMultiSelect {...filter} isOnSearch={true} />
          </div>
        );
      case "slider":
        return (
          <div className={styles.filterWrapper} key={key}>
            <label className={styles.label}>
              {filter.title}
              {filter.desc && <HelpPopOver customText={filter.desc} color="black" />}
            </label>
            <RangeSlider {...filter} isOnSearch={true} />
          </div>
        );
      case "star":
        return (
          <div className={styles.filterWrapper} key={key}>
            <label className={styles.label}>
              {filter.title}
              {filter.desc && <HelpPopOver customText={filter.desc} color="black" />}
            </label>
            <CustomStar {...filter} isOnSearch={true} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.advancedFilters}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h3 className={styles.title}>Filters</h3>
          <button
            onClick={handleClearAll}
            className={styles.clearAllButton}
            type="button"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className={styles.content}>
        {Object.entries(groupedFilters).map(([fType, typeFilters], groupIdx) => {
          if (!typeFilters || typeFilters.length === 0) return null;
          return (
            <div key={fType}>
              <h4 className={styles.sectionTitle}>
                {FILTER_TYPE_LABELS[fType] || fType}
              </h4>
              <div className={styles.sectionContent}>
                {typeFilters.map((filter, idx) => getFilter(filter, idx))}
              </div>
              {groupIdx < Object.keys(groupedFilters).length - 1 && (
                <div className={styles.divider} style={{ marginTop: 20 }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
